import React, { useEffect, useMemo } from 'react';

import Space from 'components/generic/Space.component';
import CONF from 'config/conf.singleton';
import RELEASE from 'config/release.singleton';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import useTip from 'hooks/useTip.hook';
import fromAnyToString from 'utils/converters/fromAnyToString';
import logger from 'utils/logger.util';

const L = logger('ReleaseInfoBox');

const ReleaseInfoBoxRow = ({ name, value }) => {
  const v = fromAnyToString(value);
  return (
    <tr data-bem="ReleaseInfoBoxRow">
      <td data-bem="ReleaseInfoBoxRow__name">{name}:</td>
      {v ? (
        <td data-bem="ReleaseInfoBoxRow__value--set">{v}</td>
      ) : (
        <td
          data-bem="ReleaseInfoBoxRow__value--unset"
          style={{
            color: '#CCCCCC',
          }}
        >
          (not set)
        </td>
      )}
    </tr>
  );
};

const ReleaseInfoBoxBreak = () => (
  <tr data-bem="ReleaseInfoBoxBreak">
    <td colSpan={2}>
      <hr />
    </td>
  </tr>
);

const ReleaseInfoBox = ({ extended }) => {
  const [display, setDisplay] = useStateWhileMounted('');
  const [nextSha, setNextSha] = useStateWhileMounted('');
  const [nextNow, setNextNow] = useStateWhileMounted('');
  const { val: isUpToDate, set: setIsUpToDate } = useTip(true);

  useEffect(
    () =>
      RELEASE.subscribe(() => {
        RELEASE.display().then(setDisplay);
        RELEASE.isUpToDate().then(setIsUpToDate);
        RELEASE.nextNow().then(setNextNow);
        RELEASE.nextSha().then(setNextSha);
      }),
    [setDisplay, setIsUpToDate, setNextNow, setNextSha],
  );

  // NOTE: using memo here as to only print the message whenever the values are changed, not every component render
  useMemo(() => void L.info('()', { display, isCurrent: isUpToDate }), [display, isUpToDate]);

  if (!extended) {
    return <Space>{display}</Space>;
  }

  return (
    <div data-bem="ReleaseInfoBoxRow__info">
      {!isUpToDate && (
        <>
          <p>A new version is available. Please reload the page</p>
          <br />
        </>
      )}
      <table data-bem="ReleaseInfoBoxRow__table">
        <tbody>
          <ReleaseInfoBoxRow name="Is up to date" value={isUpToDate ? 'yes' : 'no'} />
          <ReleaseInfoBoxRow name="Display" value={display} />
          <ReleaseInfoBoxBreak />
          <ReleaseInfoBoxRow name="Build" value={CONF.buildCiNum} />
          <ReleaseInfoBoxRow name="Branch" value={CONF.buildCiBranch} />
          <ReleaseInfoBoxRow name="Tag" value={CONF.buildCiTag} />
          <ReleaseInfoBoxRow name="Current Hash" value={CONF.buildShaLong} />
          <ReleaseInfoBoxRow name="Current Hash (shortened)" value={CONF.buildShaShort} />
          <ReleaseInfoBoxRow name="Current Release Time" value={CONF.buildNow} />
          <ReleaseInfoBoxBreak />
          <ReleaseInfoBoxRow name="Next Hash" value={nextSha} />
          <ReleaseInfoBoxRow name="Next Release Time" value={nextNow} />
          <ReleaseInfoBoxBreak />
          <ReleaseInfoBoxRow name="Environment" value={CONF.environment} />
          <ReleaseInfoBoxRow name="R-environment" value={CONF.reactEnv} />
          <ReleaseInfoBoxRow name="N-environment" value={CONF.nodeEnv} />
        </tbody>
      </table>
    </div>
  );
};

export default ReleaseInfoBox;
